import { HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { ApiError, ApiProgressSubject, ApiSubject, Base, IApiRequest, IRequestOptions } from '@sb/library/base';
import { Observable, Subscription, of } from 'rxjs';

export abstract class BaseApiRequestManager {

    protected static _maxParallelRequests = 10;

    protected _disabled = false;

    protected _inFlight: Set<QueueRequest> = new Set();
    protected _requestQueue: QueueRequest[] = [];

    protected abstract SendRequest<Result>(request: IApiRequest<any>, reportProgress: boolean): Observable<HttpEvent<Result>>;

    Request<Body, Result, TrackBy extends IRequestOptions>(request: IApiRequest<Body>, subject: ApiSubject<Result, TrackBy>, progressSubject: ApiProgressSubject<TrackBy> | undefined,
                                                           trackBy: TrackBy, ignoreTransitionAnimations: boolean, cancellable: boolean): () => void {
        if (this._disabled) {
            console.warn('ApiRequestManager disabled - ignoring request', request);
            progressSubject?.complete();
            return () => {};
        }

        const queueRequest = new QueueRequest({
            Request: request,
            Subject: subject,
            ProgressSubject: progressSubject,
            TrackBy: trackBy,
            IgnoreTransitionAnimations: ignoreTransitionAnimations,
            Cancellable: cancellable
        });

        if (this._inFlight.size < BaseApiRequestManager._maxParallelRequests) {
            this.RunQueueRequest(queueRequest);
        } else {
            this._requestQueue.push(queueRequest);
        }

        return () => {
            // Cancellation function
            if (this._inFlight.has(queueRequest)) {
                queueRequest.Subscription!.unsubscribe();
                queueRequest.ProgressSubject?.complete();
                this.RunNextQueued(queueRequest);
            } else {
                const index = this._requestQueue.indexOf(queueRequest);
                if (index >= 0) {
                    this._requestQueue.splice(index, 1);
                    queueRequest.ProgressSubject?.complete();
                }
            }
        }
    }

    private RunNextQueued(lastRequest: QueueRequest) {
        this._inFlight.delete(lastRequest);

        const nextRequest = this._requestQueue.shift();
        if (nextRequest) {
            this.RunQueueRequest(nextRequest);
        }
    }

    private RunQueueRequest<Result>(queueRequest: QueueRequest<Result>) {
        this._inFlight.add(queueRequest);

        try {
            const observable = this.SendRequest<Result>(queueRequest.Request, !!queueRequest.ProgressSubject);

            queueRequest.Subscription = observable.subscribe({
                next: data => {
                    switch (data.type) {
                        case HttpEventType.UploadProgress:
                            if (queueRequest.ProgressSubject?.Upload) {
                                queueRequest.ProgressSubject!.next({ Request: queueRequest.TrackBy, Response: data });
                            }
                            break;
                        case HttpEventType.DownloadProgress:
                            if (queueRequest.ProgressSubject?.Computation && queueRequest.ComputeTotal) {
                                data.total = queueRequest.ComputeTotal;
                                queueRequest.ProgressSubject!.next({ Request: queueRequest.TrackBy, Response: data });
                            }
                            break;
                        case HttpEventType.Response:
                            this.RunNextQueued(queueRequest);
                            if (queueRequest.ProgressSubject) {
                                queueRequest.ProgressSubject.complete();
                                const errorBody = data.body as any;
                                if (errorBody.ErrorCode) {   // An error occurred during the batch operation. Although a 200 was returned, we simulate an error.
                                    queueRequest.Subject.error(new ApiError({ Request: queueRequest.TrackBy, Status: errorBody.StatusCode, Message: errorBody.StatusCode, Response: errorBody }));
                                }
                            }
                            const delay = queueRequest.IgnoreTransitionAnimations ? of(undefined) : Base.transitionAnimationEnd;
                            delay.subscribe(() => queueRequest.Subject.next({ Request: queueRequest.TrackBy, Response: data.body! }));
                            break;
                        case HttpEventType.ResponseHeader:
                            if (queueRequest.ProgressSubject?.Computation) {
                                queueRequest.ComputeTotal = Number(data.headers.get('X-SchoolBox-TotalCount'));
                            }
                            break;
                    }
                },
                error: (error: HttpErrorResponse) => {
                    if (!(error.error instanceof ProgressEvent)) {  // We can safely ignore transient errors in progress events
                        this.RunNextQueued(queueRequest);
                        queueRequest.ProgressSubject?.complete();
                        queueRequest.Subject.error(new ApiError({ Request: queueRequest.TrackBy, Status: error.status, Message: error.statusText, Response: error.error }));
                    }
                }
            });
        } catch {
            setTimeout(() => {
                this.RunNextQueued(queueRequest);
                this.SendNetworkProblemError(queueRequest);
            });
        }
    }

    private SendNetworkProblemError(queueRequest: QueueRequest) {
        queueRequest.ProgressSubject?.complete();
        queueRequest.Subject.error(new ApiError({ Request: queueRequest.TrackBy, Status: -1, Message: 'Network problem', Response: null as any }));
    }

    set disabled(value: boolean) {
        if (value !== this._disabled) {
            this._disabled = value;

            if (value) {
                [...this._inFlight, ...this._requestQueue].forEach(request => {
                    request.Subscription?.unsubscribe();
                    this.SendNetworkProblemError(request);
                });

                this._inFlight.clear();
                this._requestQueue = [];
            }
        }
    }

    get disabled(): boolean {
        return this._disabled;
    }
}

class QueueRequest<Result = any> {
    Request!: IApiRequest<any>;
    Subject!: ApiSubject<Result, any>;
    ProgressSubject?: ApiProgressSubject<any>;
    ComputeTotal?: number;
    TrackBy!: IRequestOptions;
    Cancellable!: boolean;
    IgnoreTransitionAnimations!: boolean;
    Subscription?: Subscription;

    constructor(initializer: QueueRequest<Result>) {
        Object.assign(this, initializer);
    }
}
